import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Plus, Trash2, User2, Receipt, 
  Loader2, Save, Calendar, Layers, RefreshCw, 
  FileText, CheckCircle2, Calculator, Printer
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';
import FoxLogo from '../components/FoxLogo';
import HeaderActions from '../components/HeaderActions';
import Footer from '../components/Footer';

import { apiService } from '../services/api';
import { useLanguage } from '../context/LanguageContext'; 
import { useAuth } from '../context/AuthContext';

const CreateInvoicePage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  
  // 🔥 Dil Fonksiyonunu Al
  const { t } = useLanguage(); 
  
  const [loading, setLoading] = useState(false);

  const [invoiceData, setInvoiceData] = useState({
    invoice_number: `RE-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`,
    date: new Date().toISOString().split('T')[0],
    sender: { name: '', street: '', zip_city: '', country: 'Deutschland' },
    recipient: { name: '', street: '', zip_city: '', country: 'Deutschland' },
    items: [{ description: 'Beratung / Consulting', quantity: 1, unit_price: 100.00 }],
    bank_info: { bank_name: '', iban: '', bic: '', account_holder: '' },
    tax_rate: 19.0,
    sender_tax_id: '',
    is_kleinunternehmer: false
  });

  // Kullanıcı bilgilerini otomatik doldur
  useEffect(() => {
    if (user) {
      const details = user.companyDetails || {};
      setInvoiceData(prev => ({
        ...prev,
        sender: {
          name: details.companyName || user.name || '',
          street: details.street || user.address || '',
          zip_city: details.zipCity || '',
          country: details.country || 'Deutschland'
        },
        sender_tax_id: details.taxId || '',
        bank_info: {
          bank_name: details.bankName || '',
          iban: details.iban || '',
          bic: details.bic || '',
          account_holder: details.accountHolder || details.companyName || user.name || ''
        },
        is_kleinunternehmer: details.isSmallBusiness || false,
        tax_rate: details.isSmallBusiness ? 0 : 19.0
      }));
    }
  }, [user]);

  // Hesaplamalar
  const subtotal = invoiceData.items.reduce((acc, item) => acc + (item.quantity * item.unit_price), 0);
  const taxAmount = invoiceData.is_kleinunternehmer ? 0 : subtotal * (invoiceData.tax_rate / 100);
  const total = subtotal + taxAmount;

  const handleRecipientChange = (e) => {
    setInvoiceData({
      ...invoiceData,
      recipient: { ...invoiceData.recipient, [e.target.name]: e.target.value }
    });
  };

  const handleKleinunternehmerChange = (checked) => {
    setInvoiceData(prev => ({
        ...prev,
        is_kleinunternehmer: checked,
        tax_rate: checked ? 0 : 19.0
    }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...invoiceData.items];
    newItems[index][field] = value;
    setInvoiceData({ ...invoiceData, items: newItems });
  };

  const addItem = () => {
    setInvoiceData({
      ...invoiceData,
      items: [...invoiceData.items, { description: '', quantity: 1, unit_price: 0 }]
    });
  };

  const removeItem = (index) => {
    if (invoiceData.items.length > 1) {
      const newItems = invoiceData.items.filter((_, i) => i !== index);
      setInvoiceData({ ...invoiceData, items: newItems });
    }
  };

  const regenerateInvoiceNumber = () => {
     setInvoiceData(prev => ({
         ...prev,
         invoice_number: `RE-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`
     }));
  };

  const handleCreatePDF = async () => {
    if (!invoiceData.recipient.name) {
      toast({ 
          title: t('error_general') || "Error", 
          description: t('recipient_required') || "Recipient name is required.", 
          variant: "destructive" 
      });
      return;
    }
    
    setLoading(true);
    try {
      const response = await apiService.invoices.preview(invoiceData);
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Rechnung_${invoiceData.invoice_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({ 
        title: t('success_general') || "Success", 
        description: t('invoice_created') || "Invoice created successfully.", 
        className: "bg-emerald-500 text-white border-none" 
      });
    } catch (error) {
      console.error("PDF Generation failed:", error);
      toast({ 
        title: t('error_general') || "System Error", 
        description: error.response?.data?.detail || "PDF engine failed.", 
        variant: "destructive" 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100 font-sans pb-32 relative flex flex-col selection:bg-blue-500/30">
      <Toaster />
      
      {/* HEADER */}
      <header className="sticky top-0 z-50 bg-[#0B1120]/80 backdrop-blur-xl border-b border-white/5 px-6 py-4">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
            <div className="flex items-center gap-4">
                <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-white/10 rounded-xl transition-all text-slate-400 hover:text-white active:scale-90">
                    <ArrowLeft size={20} />
                </button>
                <div>
                    <h1 className="text-xl font-black tracking-tighter text-white uppercase flex items-center gap-2">
                        INVOICE <span className="text-blue-500 italic">GENIUS</span>
                    </h1>
                    <div className="flex items-center gap-2 text-[10px] font-mono text-slate-500">
                        <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></span>
                        DIN 5008 COMPLIANT
                    </div>
                </div>
            </div>
            <div className="flex items-center gap-3">
                <HeaderActions />
            </div>
        </div>
      </header>

      <main className="flex-1 max-w-[1600px] mx-auto p-6 md:p-8 grid grid-cols-1 lg:grid-cols-2 gap-12 relative z-10 w-full">
        
        {/* LEFT: FORM SECTION */}
        <div className="space-y-8">
            
            {/* 1. METADATA */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="glass-panel rounded-[2.5rem] p-8 border-white/5 bg-white/[0.02]">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-3">
                        <Label className="text-[10px] font-black uppercase text-slate-500 tracking-[0.2em]">{t('customer_no') || 'Invoice Number'}</Label>
                        <div className="flex gap-2">
                            <div className="flex-1 h-14 bg-black/20 border border-white/10 rounded-2xl flex items-center px-4 font-mono text-blue-400 text-sm font-black shadow-inner">
                                {invoiceData.invoice_number}
                            </div>
                            <button onClick={regenerateInvoiceNumber} className="p-4 bg-white/5 hover:bg-blue-600/20 border border-white/10 rounded-2xl text-slate-400 hover:text-blue-400 transition-all active:rotate-180 duration-500">
                                <RefreshCw size={20} />
                            </button>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <Label className="text-[10px] font-black uppercase text-slate-500 tracking-[0.2em]">{t('label_date') || 'Date'}</Label>
                        <div className="relative">
                            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} />
                            <Input 
                                type="date" 
                                value={invoiceData.date} 
                                onChange={(e) => setInvoiceData({...invoiceData, date: e.target.value})}
                                className="pl-12 h-14 bg-black/20 border-white/10 rounded-2xl text-white font-mono focus:ring-2 ring-blue-500/20"
                            />
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* 2. RECIPIENT */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="glass-panel rounded-[2.5rem] overflow-hidden border-white/5">
                <div className="p-6 border-b border-white/5 bg-white/[0.03] flex items-center gap-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg text-blue-400"><User2 size={18} /></div>
                    <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-300">{t('recipient_label') || 'Recipient'}</h3>
                </div>
                <div className="p-8 space-y-6">
                    <div className="space-y-2">
                        <Label className="text-[10px] font-black uppercase text-slate-500">{t('company') || 'Company / Name'}</Label>
                        <Input name="name" placeholder="Max Mustermann GmbH" value={invoiceData.recipient.name} onChange={handleRecipientChange} className="h-14 bg-black/20 border-white/10 rounded-2xl text-white font-bold placeholder:text-slate-700 focus:bg-blue-500/5"/>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <Label className="text-[10px] font-black uppercase text-slate-500">{t('address') || 'Street & Nr'}</Label>
                            <Input name="street" placeholder="Musterstr. 1" value={invoiceData.recipient.street} onChange={handleRecipientChange} className="h-14 bg-black/20 border-white/10 rounded-2xl text-white placeholder:text-slate-700"/>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-[10px] font-black uppercase text-slate-500">{t('label_zip_city') || 'ZIP & City'}</Label>
                            <Input name="zip_city" placeholder="10115 Berlin" value={invoiceData.recipient.zip_city} onChange={handleRecipientChange} className="h-14 bg-black/20 border-white/10 rounded-2xl text-white placeholder:text-slate-700"/>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* 3. ITEMS */}
            <div className="space-y-4">
                <div className="flex items-center justify-between px-4">
                    <h3 className="text-xs font-black uppercase tracking-[0.2em] text-slate-500 flex items-center gap-2">
                        <Layers size={14} /> {t('official_procedures') || 'Services'}
                    </h3>
                    <Button onClick={addItem} variant="ghost" className="h-10 text-[10px] font-black uppercase tracking-widest text-blue-400 hover:bg-blue-500/10 rounded-xl">
                        <Plus size={16} className="mr-2" /> {t('action_next') || 'Add Item'}
                    </Button>
                </div>
                
                <AnimatePresence>
                    {invoiceData.items.map((item, index) => (
                        <motion.div 
                            key={index}
                            initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, scale: 0.95 }}
                            className="glass-panel rounded-3xl p-5 flex flex-col md:flex-row gap-5 group hover:border-blue-500/20 transition-all bg-white/[0.01]"
                        >
                            <div className="flex-1 space-y-2">
                                <Label className="text-[9px] uppercase text-slate-600 font-black tracking-widest">{t('label_merchant') || 'Description'}</Label>
                                <Input 
                                    value={item.description} 
                                    onChange={(e) => handleItemChange(index, 'description', e.target.value)} 
                                    className="h-12 bg-black/20 border-white/5 rounded-xl text-white text-sm"
                                    placeholder="Service description..."
                                />
                            </div>
                            <div className="w-24 space-y-2">
                                <Label className="text-[9px] uppercase text-slate-600 font-black tracking-widest text-center block">{t('quantity') || 'Qty'}</Label>
                                <Input 
                                    type="number" 
                                    value={item.quantity} 
                                    onChange={(e) => handleItemChange(index, 'quantity', parseFloat(e.target.value) || 0)} 
                                    className="h-12 bg-black/20 border-white/5 rounded-xl text-white text-center font-mono"
                                />
                            </div>
                            <div className="w-32 space-y-2">
                                <Label className="text-[9px] uppercase text-slate-600 font-black tracking-widest text-right block">{t('label_amount') || 'Price'} (€)</Label>
                                <Input 
                                    type="number" 
                                    value={item.unit_price} 
                                    onChange={(e) => handleItemChange(index, 'unit_price', parseFloat(e.target.value) || 0)} 
                                    className="h-12 bg-black/20 border-white/5 rounded-xl text-white text-right font-mono"
                                />
                            </div>
                            <div className="flex items-end pb-1">
                                <button onClick={() => removeItem(index)} className="p-3 text-slate-600 hover:text-red-500 hover:bg-red-500/10 rounded-xl transition-all active:scale-90">
                                    <Trash2 size={18} />
                                </button>
                            </div>
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>

            {/* 4. SETTINGS */}
            <div className="glass-panel rounded-[2rem] p-6 flex items-center justify-between border-orange-500/10 bg-orange-500/[0.02]">
                <div className="flex items-center gap-4">
                    <div className="p-3 bg-orange-500/10 rounded-2xl text-orange-500 shadow-inner">
                        <Receipt size={22} />
                    </div>
                    <div>
                        <h4 className="text-sm font-black text-white uppercase tracking-tight">Kleinunternehmerregelung</h4>
                        <p className="text-[10px] text-slate-500 font-bold uppercase">§ 19 UStG ({t('status_deductible') || 'Tax Exempt'})</p>
                    </div>
                </div>
                <Switch checked={invoiceData.is_kleinunternehmer} onCheckedChange={handleKleinunternehmerChange} />
            </div>

        </div>

        {/* RIGHT: LIVE PREVIEW */}
        <div className="lg:sticky lg:top-28 h-fit space-y-8">
            
            {/* PAPER PREVIEW */}
            <div className="bg-white text-slate-900 rounded-sm shadow-[0_20px_50px_rgba(0,0,0,0.5)] overflow-hidden aspect-[210/297] flex flex-col p-12 relative border border-slate-200">
                <div className="flex justify-between items-start mb-16">
                    <div className="max-w-[60%]">
                        <h2 className="text-xl font-black text-slate-900 leading-tight uppercase">{invoiceData.sender.name || "YOUR NAME"}</h2>
                        <p className="text-[10px] text-slate-500 leading-relaxed mt-2 italic border-l-2 border-blue-500 pl-3">
                           {invoiceData.sender.street}<br/>
                           {invoiceData.sender.zip_city}
                        </p>
                    </div>
                    <div className="text-right">
                        <FoxLogo size={48} className="text-blue-600 ml-auto" />
                        <div className="mt-4 space-y-1">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('customer_no') || 'Invoice No'}</p>
                          <p className="text-xs font-bold font-mono">{invoiceData.invoice_number}</p>
                        </div>
                    </div>
                </div>

                <div className="mb-16">
                    <p className="text-[9px] font-black text-blue-600 uppercase tracking-tighter mb-3 border-b pb-1 inline-block">{t('recipient_label') || 'RECIPIENT'}</p>
                    <p className="font-black text-base uppercase tracking-tight">{invoiceData.recipient.name || "CLIENT NAME"}</p>
                    <p className="text-xs text-slate-600 mt-1">{invoiceData.recipient.street}</p>
                    <p className="text-xs text-slate-600 font-bold">{invoiceData.recipient.zip_city}</p>
                </div>

                <div className="flex-1">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b-2 border-slate-900">
                                <th className="py-3 text-[10px] font-black uppercase text-slate-400 text-left">{t('label_merchant') || 'Description'}</th>
                                <th className="py-3 text-[10px] font-black uppercase text-slate-400 text-center">{t('quantity') || 'Qty'}</th>
                                <th className="py-3 text-[10px] font-black uppercase text-slate-400 text-right">{t('label_amount') || 'Unit'}</th>
                                <th className="py-3 text-[10px] font-black uppercase text-slate-400 text-right">{t('total_expenses') || 'Total'}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {invoiceData.items.map((item, i) => (
                                <tr key={i}>
                                    <td className="py-4 text-xs font-bold text-slate-800">{item.description}</td>
                                    <td className="py-4 text-xs text-slate-500 text-center font-mono">{item.quantity}</td>
                                    <td className="py-4 text-xs text-slate-500 text-right font-mono">{item.unit_price.toFixed(2)}</td>
                                    <td className="py-4 text-xs font-black text-slate-900 text-right font-mono">{(item.quantity * item.unit_price).toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-8 border-t-2 border-slate-900 pt-6 flex flex-col items-end gap-3">
                    <div className="flex justify-between w-56 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                        <span>Net Total</span>
                        <span>{subtotal.toFixed(2)} €</span>
                    </div>
                    {!invoiceData.is_kleinunternehmer && (
                         <div className="flex justify-between w-56 text-[10px] font-bold text-slate-500 uppercase tracking-widest">
                            <span>{t('category_tax') || 'VAT'} ({invoiceData.tax_rate}%)</span>
                            <span>{taxAmount.toFixed(2)} €</span>
                        </div>
                    )}
                    <div className="flex justify-between w-64 text-2xl font-black text-blue-600 mt-2 border-t border-slate-100 pt-3">
                        <span className="text-xs self-center text-slate-400 font-bold uppercase tracking-tighter">{t('total_expenses') || 'Total'}</span>
                        <span className="tabular-nums">{total.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €</span>
                    </div>
                    {invoiceData.is_kleinunternehmer && (
                        <p className="text-[8px] text-slate-400 italic mt-4 w-full text-right">
                          * {t('legal_disclaimer_point_1') || 'Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.'}
                        </p>
                    )}
                </div>
            </div>

            {/* CTA PANEL */}
            <motion.div whileHover={{ y: -5 }} className="glass-panel p-8 rounded-[2.5rem] shadow-2xl border-blue-500/20 bg-gradient-to-br from-blue-600/10 to-transparent">
                 <div className="flex justify-between items-center mb-8">
                     <div className="text-left">
                         <p className="text-[10px] text-blue-400 uppercase tracking-[0.3em] font-black mb-1">{t('ready_to_scan') || 'READY'}</p>
                         <p className="text-4xl font-black text-white tabular-nums tracking-tighter">
                            {total.toLocaleString('de-DE', { minimumFractionDigits: 2 })} €
                         </p>
                     </div>
                     <div className="p-4 bg-blue-500/10 rounded-3xl border border-blue-500/20">
                        <Calculator className="text-blue-400" size={32} />
                     </div>
                 </div>
                 
                 <Button 
                   onClick={handleCreatePDF} 
                   disabled={loading} 
                   className="w-full h-20 bg-blue-600 hover:bg-blue-500 text-white font-black uppercase tracking-[0.2em] rounded-2xl shadow-[0_15px_30px_rgba(37,99,235,0.4)] transition-all active:scale-[0.98] flex items-center justify-center gap-4 text-sm"
                 >
                     {loading ? (
                         <Loader2 className="animate-spin" size={24} />
                     ) : (
                         <>
                            <FileText size={24} className="group-hover:rotate-12 transition-transform" /> 
                            {t('action_download') || 'Download PDF'}
                         </>
                     )}
                 </Button>
            </motion.div>

        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default CreateInvoicePage;